import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {GridColDef, GridFilterModel, GridSortModel} from '@mui/x-data-grid';
import './JobResultDataGrid.scss';
import {tableProps} from '../../job-list-layout/utils';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import {getColumns} from './issueCodeColumn';
import {relevancyColumns} from './relevancyColumn';
import {DataGridRowSelectionModel} from '../../../../../shared/interfaces';
import {
  useGetAIReviewJobResultIssueCodeList,
  useGetAIReviewJobResultRelevancyList,
} from '../../../../../hooks/ai/review/useReview';
import {
  mapGridFiltersToColumnFilters,
  mapGridFiltersToNavigateColumnFilters,
  RELEVANCY_COLUMN_MAP,
  RELEVANCY_NAVIGATE_COLUMN_MAP,
  VIEW_BY_ISSUE_CODE_COLUMN_MAP,
} from '../../utils';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {
  IGetMasterDocIdForIssueCodingPayload,
  IGetMasterDocIdForRelevancyPayloadBody,
  IAIReviewRelevancyDocSelection,
} from '../../../../../api/types/aiReview.type';
import {ReactComponent as NoDataFoundIcon} from '../../../../../assets/images/no-data-found.svg';
import {
  IPayload,
  IPreviewDoc,
  JobResultViewType,
  PAYLOAD_TYPE,
} from '../JobResults';
import {ViewInDocListAction} from '../job-result-tabs/JobResultTabs';

interface IJobList {
  jobId: string | number;
  type: JobResultViewType;
  handleSetStates: (
    payload: IPayload,
    type: PAYLOAD_TYPE,
    docsToView?: number,
    destination?: 'DOCLIST' | 'DOCVIEW'
  ) => void;
  onViewInDocListActionChange?: (action: ViewInDocListAction) => void;
  onOpenRelevancyDocPanel?: (selection: IAIReviewRelevancyDocSelection) => void;
  setSelectedDocForPreview?: React.Dispatch<
    React.SetStateAction<IPreviewDoc | null>
  >;
}

type ToastState = {
  open: boolean;
  message: string;
  status: ToastType;
};

const defaultToastState: ToastState = {
  open: false,
  message: '',
  status: ToastType.Success,
};

const ISSUE_CODING = 'Issue Coding';
const RELEVANCY = 'Relevancy';

const JobResultDataGrid = ({
  jobId,
  type,
  handleSetStates,
  onViewInDocListActionChange,
  onOpenRelevancyDocPanel,
  setSelectedDocForPreview,
}: IJobList) => {
  const [pageSize, setPageSize] = useState(tableProps.DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: type === ISSUE_CODING ? 'reviewJobIssueCode' : 'docId',
      sort: type === ISSUE_CODING ? 'desc' : 'asc',
    },
  ]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [unselectedIds, setUnselectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const isIssueCoding = type === ISSUE_CODING;
  const isRelevancy = type === RELEVANCY;

  const [toast, setToast] = useState<ToastState>(defaultToastState);

  const showErrorToast = useCallback((message: string) => {
    setToast({
      open: true,
      message,
      status: ToastType.Error,
    });
  }, []);

  const relevancyPayload = useMemo(
    () => ({
      sortingColumn:
        RELEVANCY_COLUMN_MAP[sortModel[0]?.field] ?? sortModel[0]?.field,
      direction: sortModel[0]?.sort ?? 'asc',
      displayLength: pageSize,
      pageNumber,
      columnFilter: mapGridFiltersToColumnFilters(
        filterModel.items,
        RELEVANCY_COLUMN_MAP
      ),
    }),
    [sortModel, filterModel, pageSize, pageNumber]
  );

  const issueCodePayload = useMemo(
    () => ({
      sortingColumn:
        VIEW_BY_ISSUE_CODE_COLUMN_MAP[sortModel[0]?.field] ??
        sortModel[0]?.field ??
        'issueCode',
      direction: sortModel[0]?.sort ?? 'desc',
      columnFilter: mapGridFiltersToColumnFilters(
        filterModel.items,
        VIEW_BY_ISSUE_CODE_COLUMN_MAP
      ),
    }),
    [sortModel, filterModel]
  );

  const {
    data: issueCodeResponse,
    isLoading: isIssueCodeLoading,
    isError: isIssueCodeError,
  } = useGetAIReviewJobResultIssueCodeList(
    jobId,
    issueCodePayload,
    isIssueCoding
  );

  const {
    data: relevancyResponse,
    isLoading: isRelevancyLoading,
    isFetching: isRelevancyFetching,
    isError: isRelevancyError,
  } = useGetAIReviewJobResultRelevancyList(
    jobId,
    relevancyPayload,
    isRelevancy
  );

  const renderNoRowsOverlay = useCallback(
    () => (
      <Box className="no-grid-overlay" data-testid="relevancy-no-data-overlay">
        <NoDataFoundIcon aria-hidden="true" />
        <Typography
          className="typo-text headline3_size"
          data-testid="relevancy-no-data-message"
        >
          {t('ai.review.jobResult.messageNoData')}
        </Typography>
      </Box>
    ),
    []
  );

  const rows = useMemo(() => {
    if (isIssueCoding) {
      return issueCodeResponse?.issueCodeResults ?? [];
    }
    if (isRelevancy) {
      return isRelevancyError ? [] : (relevancyResponse?.documents ?? []);
    }
    return [];
  }, [
    isIssueCoding,
    isRelevancy,
    isRelevancyError,
    issueCodeResponse,
    relevancyResponse,
  ]);

  const shouldClearRelevancySelection =
    isRelevancy &&
    (isRelevancyError ||
      (!isRelevancyLoading && !isRelevancyFetching && rows.length === 0));
  const relevancyRowCount =
    isRelevancyError || rows.length === 0
      ? 0
      : (relevancyResponse?.totalDocs ?? 0);

  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>(
    {}
  );

  const handleCellCheckboxChange = useCallback(
    (rowId: number, columnKey: string, checked: boolean) => {
      setCheckboxState((prev) => ({
        ...prev,
        [`${rowId}-${columnKey}`]: checked,
      }));
    },
    []
  );

  const handleHeaderCheckboxChange = useCallback(
    (columnKey: string, checked: boolean) => {
      setCheckboxState((prev) => {
        const next = {...prev};

        rows.forEach(
          (row: {reviewJobIssueCodeID?: number; docId?: string | number}) => {
            const rowId = isRelevancy ? row.docId : row.reviewJobIssueCodeID;
            if (rowId == null) return;
            // skip rows with zero count for this column
            if ((row as Record<string, unknown>)[columnKey] === 0) return;
            next[`${rowId}-${columnKey}`] = checked;
          }
        );

        return next;
      });
    },
    [isRelevancy, rows]
  );

  const handleSelectionStateChange = useCallback(
    (state: DataGridRowSelectionModel) => {
      setSelectedIds(state.selectedIds);
      setUnselectedIds(state.unselectedIds);
      setIsAllSelected(state.isAllSelected);
    },
    []
  );

  useEffect(() => {
    if (!shouldClearRelevancySelection) return;

    setSelectedIds([]);
    setUnselectedIds([]);
    setIsAllSelected(false);
  }, [shouldClearRelevancySelection]);

  const isViewInDocListEnabled = useMemo(() => {
    if (isRelevancy) {
      return (
        !shouldClearRelevancySelection &&
        (isAllSelected || selectedIds.length > 0)
      );
    }

    if (isIssueCoding) {
      return Object.values(checkboxState).some(Boolean);
    }

    return false;
  }, [
    isIssueCoding,
    isRelevancy,
    shouldClearRelevancySelection,
    isAllSelected,
    selectedIds,
    checkboxState,
  ]);

  useEffect(() => {
    if (isIssueCodeError || isRelevancyError) {
      showErrorToast(t('ai.review.jobResult.error.failedToFetch'));
      return;
    }
  }, [isIssueCodeError, isRelevancyError, showErrorToast]);

  const handleCountCellClick = useCallback(
    (rowId: number, isRelevant: boolean, count: number) => {
      if (!count) return; // guard: never navigate for a zero count
      const payloadBody = [{reviewJobIssueCodeId: rowId, isRelevant}];

      const payload: IGetMasterDocIdForIssueCodingPayload = {
        jobId,
        payloadBody,
      };

      handleSetStates(
        payload,
        PAYLOAD_TYPE.DOC_RESULT_ISSUE_CODE,
        count,
        'DOCLIST'
      );
    },
    [handleSetStates, jobId]
  );

  const handleDocViewer = (props: IPreviewDoc) => {
    setSelectedDocForPreview?.({
      ...props,
    });
  };
  const columns: GridColDef[] = useMemo(() => {
    return isIssueCoding
      ? getColumns(
          checkboxState,
          handleCellCheckboxChange,
          handleHeaderCheckboxChange,
          rows,
          handleCountCellClick
        )
      : relevancyColumns(handleDocViewer, onOpenRelevancyDocPanel);
  }, [
    type,
    checkboxState,
    handleCellCheckboxChange,
    handleHeaderCheckboxChange,
    rows,
    handleCountCellClick,
    onOpenRelevancyDocPanel,
  ]);

  const handleViewInDocList = useCallback(() => {
    if (isRelevancy) {
      const payloadBody: IGetMasterDocIdForRelevancyPayloadBody = {
        isInverse: isAllSelected,
        selectedDocIDs: !isAllSelected ? selectedIds.join(',') : '',
        excludedDocIDs: isAllSelected ? unselectedIds.join(',') : '',
        documentCount: 0,
        columnFilters: mapGridFiltersToNavigateColumnFilters(
          filterModel.items,
          RELEVANCY_NAVIGATE_COLUMN_MAP
        ),
      };
      const totalDocsToView =
        isAllSelected && relevancyResponse
          ? relevancyResponse?.totalDocs - unselectedIds.length
          : selectedIds.length;
      handleSetStates(
        {payloadBody, jobId},
        PAYLOAD_TYPE.DOC_RESULT_RELEVANCY,
        totalDocsToView,
        'DOCLIST'
      );
      return;
    }
    if (isIssueCoding) {
      const issueRows = issueCodeResponse?.issueCodeResults ?? [];
      const payloadBody = issueRows.flatMap((row) => {
        const entries: {
          reviewJobIssueCodeId: number;
          isRelevant: boolean;
        }[] = [];
        const id = row.reviewJobIssueCodeID;

        if (checkboxState[`${id}-relevantCount`]) {
          entries.push({reviewJobIssueCodeId: id, isRelevant: true});
        }
        if (checkboxState[`${id}-nonRelevantCount`]) {
          entries.push({reviewJobIssueCodeId: id, isRelevant: false});
        }

        return entries;
      });

      const totalDocsToView = payloadBody.reduce((sum, entry) => {
        const row = issueRows.find(
          (item) => item.reviewJobIssueCodeID === entry.reviewJobIssueCodeId
        );
        if (!row) return sum;
        return (
          sum + (entry.isRelevant ? row.relevantCount : row.nonRelevantCount)
        );
      }, 0);

      const payload: IGetMasterDocIdForIssueCodingPayload = {
        jobId,
        payloadBody,
      };
      handleSetStates(
        payload,
        PAYLOAD_TYPE.DOC_RESULT_ISSUE_CODE,
        totalDocsToView,
        'DOCLIST'
      );
      return;
    }
  }, [
    checkboxState,
    handleSetStates,
    isAllSelected,
    isIssueCoding,
    isRelevancy,
    issueCodeResponse,
    jobId,
    filterModel.items,
    relevancyResponse,
    selectedIds,
    unselectedIds,
  ]);

  useEffect(() => {
    onViewInDocListActionChange?.({
      enabled: isViewInDocListEnabled,
      onClick: handleViewInDocList,
    });
  }, [
    handleViewInDocList,
    isViewInDocListEnabled,
    onViewInDocListActionChange,
  ]);

  return (
    <>
      <Box
        className={`issue-code-container${
          isRelevancy ? ' relevancy-results-container' : ''
        }`}
        data-testid="job-result-data-grid-container"
      >
        <ArrDataGridPro
          rowHeight={tableProps.ROW_HEIGHT}
          rows={rows ?? []}
          columns={columns}
          getRowClassName={(params) => {
            if (isAllSelected) {
              return 'row-checkbox-selected';
            }
            return selectedIds.includes(params.id as string)
              ? 'row-checkbox-selected'
              : '';
          }}
          rowCount={
            isRelevancy
              ? relevancyRowCount
              : (issueCodeResponse?.issueCodeResults?.length ?? 0)
          }
          primaryKey={isRelevancy ? 'documentId' : 'reviewJobIssueCodeID'}
          pagination={!isIssueCoding}
          hideFooter={isIssueCoding}
          hideFooterPagination={isIssueCoding}
          alwaysShowPagination={!isIssueCoding}
          {...(!isIssueCoding
            ? {
                paginationMode: isRelevancy ? 'server' : 'client',
                paginationModel: {
                  page: Math.max(pageNumber - 1, 0),
                  pageSize,
                },
                itemsPerPage: pageSize,
                setItemsPerPage: setPageSize,
                currentPage: pageNumber,
                showPerPageOptions: true,
                pageSizeOptions: tableProps.PAGE_SIZE_OPTIONS,
                onPageChange: setPageNumber,
              }
            : {})}
          sortingMode="server"
          filterMode="server"
          loading={
            (isIssueCoding && isIssueCodeLoading) ||
            (isRelevancy && (isRelevancyLoading || isRelevancyFetching))
          }
          enableCheckbox={isRelevancy}
          selectedIds={selectedIds}
          unselectedIds={unselectedIds}
          isAllSelected={isAllSelected}
          onSelectionStateChange={handleSelectionStateChange}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          headerFilters
          columnHeaderHeight={tableProps.FILTER_HEADER_HEIGHT}
          headerFilterHeight={tableProps.FILTER_HEADER_HEIGHT}
          disableVirtualization
          disableColumnResize={false}
          disableColumnReorder
          disableColumnFilter
          noRowsOverlayCustomText={t('ai.review.jobResult.messageNoData')}
          noRowsOverlay={renderNoRowsOverlay}
          showCellVerticalBorder
          entityType={t('common.entries')}
          className={[
            rows.length > 0 ? 'job-result-grid--has-rows' : '',
            isIssueCoding ? 'job-result-grid--no-pagination' : '',
          ]
            .filter(Boolean)
            .join(' ') || undefined}
          getRowId={(row) =>
            isRelevancy
              ? String(row.documentId || row.docId)
              : row.reviewJobIssueCodeID
          }
        />
      </Box>

      {toast.open && (
        <ArrToastNotification
          message={toast.message}
          status={[toast.status]}
          onClose={() => setToast(defaultToastState)}
        />
      )}
    </>
  );
};

export default JobResultDataGrid;
